/**
 * Self-Healing Trigger Engine - Queues and prioritises healing cycle triggers.
 *
 * GOVERNANCE RULE (Phase 2-2.5 Containment):
 *   Healing-originated state changes must NEVER re-trigger healing cycles.
 *   The healingCycleActive flag provides a structural guard: any PROJECT_CHANGE
 *   trigger received while a healing cycle is running is suppressed before queuing.
 *   This is structural - not just a timing-based cooldown.
 *
 *   setHealingCycleActive(true) is called by autoHealRunner before executing a
 *   cycle; setHealingCycleActive(false) is called in the finally block after.
 *   This ensures the guard is always released even on cycle failure or timeout.
 */

import { runAutoHealingOnce } from "./autoHealRunner";
import { logger }              from "@/system/logger";

/* =========================================================
   EVENT TYPES
   ========================================================= */

export type TriggerEvent =
  | "PROJECT_CHANGE"
  | "BUILD_ERROR"
  | "USER_ACTION"
  | "SAVE"
  | "DEPLOY";

/* =========================================================
   PRIORITY SYSTEM
   ========================================================= */

const PRIORITY: Record<TriggerEvent, number> = {
  BUILD_ERROR:    5,
  DEPLOY:         4,
  SAVE:           3,
  PROJECT_CHANGE: 2,
  USER_ACTION:    1,
};

/* =========================================================
   STATE
   ========================================================= */

let lastTriggerAt   = 0;
let isProcessing    = false;

const COOLDOWN = 2000;

const queue: TriggerEvent[] = [];

/* =========================================================
   STRUCTURAL FEEDBACK-LOOP GUARD

   Set to true while a healing cycle is executing.
   PROJECT_CHANGE triggers are structurally suppressed when true -
   this prevents healing-applied mutations from re-triggering healing.

   This guard is structural: the suppression happens before the queue,
   before the cooldown check, before any processing. It cannot be
   circumvented by timing.
   ========================================================= */

let healingCycleActive = false;

/**
 * Called by autoHealRunner before/after each healing cycle.
 * Exported for test isolation and autoHealRunner integration.
 */
export function setHealingCycleActive(active: boolean): void {
  healingCycleActive = active;
  if (active) {
    logger.debug("[TriggerEngine] Healing cycle started - PROJECT_CHANGE triggers suppressed");
  }
}

/** Resets all module-level state. Test use only. */
export function _resetForTest(): void {
  lastTriggerAt      = 0;
  isProcessing       = false;
  queue.length       = 0;
  healingCycleActive = false;
}

/* =========================================================
   QUEUE PROCESSOR
   ========================================================= */

async function processQueue(): Promise<void> {
  if (isProcessing) return;
  if (queue.length === 0) return;

  isProcessing = true;

  try {
    queue.sort((a, b) => PRIORITY[b] - PRIORITY[a]);
    const event = queue.shift();
    if (!event) return;

    logger.debug(`[TriggerEngine] Processing trigger: ${event}`);
    await runAutoHealingOnce();

  } catch (err) {
    logger.error("[TriggerEngine] Queue processing failed:", err);
  } finally {
    isProcessing = false;
    if (queue.length > 0) {
      processQueue();
    }
  }
}

/* =========================================================
   TRIGGER ENGINE
   ========================================================= */

export function triggerSelfHealing(event: TriggerEvent): void {
  // ── STRUCTURAL LOOP GUARD ────────────────────────────────────────────────
  // PROJECT_CHANGE events emitted during an active healing cycle are structurally
  // suppressed. This prevents healing mutations from recursively triggering new
  // healing cycles. Other event types (BUILD_ERROR, DEPLOY) are not suppressed
  // because they represent external signals, not healing side-effects.
  if (healingCycleActive && event === "PROJECT_CHANGE") {
    logger.debug(
      "[TriggerEngine] PROJECT_CHANGE suppressed - healing cycle active (loop guard)"
    );
    return;
  }

  const now = Date.now();

  // Cooldown: secondary timing guard (backup to structural guard above)
  if (now - lastTriggerAt < COOLDOWN) {
    logger.debug(`[TriggerEngine] Trigger skipped (cooldown): ${event}`);
    return;
  }

  lastTriggerAt = now;

  // Deduplication: prevent the same event type appearing twice in queue
  if (queue.includes(event)) {
    logger.debug(`[TriggerEngine] Duplicate event skipped: ${event}`);
    return;
  }

  logger.debug(`[TriggerEngine] Trigger received: ${event}`);

  queue.push(event);
  processQueue();
}
